import { useState } from 'react'
import { Badge, Modal, Table, Alert } from 'reactstrap'
import { UserApiDto } from 'src/api-dtos/user/user-api.dto.interface'
import {
  resetMobile2fa,
  updateMobile2faNumber,
} from 'src/helpers/lopay_api_helper'

interface UserDetailsModalProps {
  user: UserApiDto | undefined
  onClose: () => void
  history: any
}

interface MerchantRowProps {
  merchant: {
    id: string
    name: string
    isOwner: boolean
  }
  userId: string
  onViewMerchant: (merchantId: string) => void
}

interface ErrorState {
  message: string
  isVisible: boolean
}

// Utility functions
const getFullName = (user: UserApiDto) => `${user.firstName} ${user.lastName}`

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error('Failed to copy text:', err)
    return false
  }
}

// Sub-components
const MerchantRow = ({
  merchant,
  userId,
  onViewMerchant,
}: MerchantRowProps) => (
  <tr key={`${userId}=${merchant.id}`}>
    <th scope="row">{merchant.name}</th>
    <td>{merchant.isOwner ? '✅' : '❌'}</td>
    <td>
      <button
        onClick={(e) => {
          e.preventDefault()
          onViewMerchant(merchant.id)
        }}
        type="button"
        className="btn btn-sm btn-secondary"
      >
        View Merchant
      </button>
    </td>
  </tr>
)

const SecuritySection = ({
  user,
  phoneNumber,
  onPhoneNumberChange,
  onSavePhoneNumber,
  isSaving,
  onResetMobile2FA,
  resetAreYouSure,
  mobile2faReset,
}: {
  user: UserApiDto
  phoneNumber: string
  onPhoneNumberChange: (value: string) => void
  onSavePhoneNumber: () => void
  isSaving: boolean
  onResetMobile2FA: () => void
  resetAreYouSure: boolean
  mobile2faReset: boolean
}) => (
  <div>
    <h5 className="font-size-14 mb-1">2FA</h5>
    <div>
      <span>
        Mobile 2FA Enabled:{' '}
        {user.security.smsEnabled && !mobile2faReset
          ? `✅ (${user.security.phone})`
          : '❌'}
      </span>
      <br />
      <span>
        Passcode Enabled: {user.security.passcodeEnabled ? '✅' : '❌'}
      </span>
      <br />
      <span>
        Biometrics Enabled: {user.security.biometricsEnabled ? '✅' : '❌'}
      </span>
      <br />
      <span>In Grace Period: {user.security.inGracePeriod ? '✅' : '❌'}</span>
    </div>
    <br />
    {!mobile2faReset && user.security.phone && (
      <button
        onClick={(e) => {
          e.preventDefault()
          onResetMobile2FA()
        }}
        type="button"
        className="btn btn-sm btn-danger"
      >
        {resetAreYouSure
          ? 'Click here again to confirm 2FA reset'
          : 'Reset Mobile 2FA'}
      </button>
    )}
    {user.security.smsEnabled && (
      <div className="mt-2 d-flex gap-2">
        <input
          type="tel"
          className="form-control"
          placeholder="Enter phone number"
          value={phoneNumber}
          onChange={(e) => onPhoneNumberChange(e.target.value)}
        />
        <button
          onClick={onSavePhoneNumber}
          disabled={isSaving}
          className="btn btn-primary"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    )}
  </div>
)

export const UserDetailsModal = ({
  user,
  onClose,
  history,
}: UserDetailsModalProps) => {
  const [copyUserIdText, setCopyUserIdText] = useState<string>('Copy')
  const [resetAreYouSure, setResetAreYouSure] = useState(false)
  const [mobile2faReset, setMobile2faReset] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState<string>(
    user?.security?.phone || '',
  )
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<ErrorState>({
    message: '',
    isVisible: false,
  })

  const showError = (message: string) => {
    setError({ message, isVisible: true })
    setTimeout(() => setError((prev) => ({ ...prev, isVisible: false })), 5000)
  }

  const handleCopyUserId = async (id: string) => {
    const success = await copyToClipboard(id)
    setCopyUserIdText(success ? 'Copied 🎉' : 'Could not copy 😭')
    setTimeout(() => setCopyUserIdText('Copy'), 2500)
  }

  const handleResetMobile2FA = async () => {
    if (!user) return

    if (!resetAreYouSure) {
      setResetAreYouSure(true)
      return
    }

    try {
      await resetMobile2fa(user.id)
      setResetAreYouSure(false)
      setMobile2faReset(true)
    } catch (error) {
      console.error('Failed to reset mobile 2FA:', error)
      showError('Failed to reset mobile 2FA. Please try again.')
    }
  }

  const handleSavePhoneNumber = async () => {
    if (!user) return
    setIsSaving(true)
    try {
      await updateMobile2faNumber(user.id, phoneNumber)
    } catch (error) {
      console.error('Failed to update phone number:', error)
      showError('Failed to update phone number. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleViewMerchant = (merchantId: string) => {
    history.push('/merchant/' + merchantId)
  }

  const handleClose = () => {
    onClose()
    setResetAreYouSure(false)
    setMobile2faReset(false)
  }

  if (!user) return null

  return (
    <Modal isOpen={!!user} toggle={handleClose} scrollable={true}>
      <div className="modal-header">
        <h5 className="modal-title mt-0">{getFullName(user)}</h5>
        <button
          type="button"
          onClick={handleClose}
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="modal-body">
        {error.isVisible && (
          <Alert color="danger" className="mb-3">
            {error.message}
          </Alert>
        )}
        <div>
          <div className="mb-4">
            <h5 className="font-size-14 mb-1">User Details</h5>
            <p className="card-title-desc mb-2">
              ID: {user.id}{' '}
              <b>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => handleCopyUserId(user.id)}
                >
                  [{copyUserIdText}]
                </button>
              </b>
            </p>
            <div>
              <span>{getFullName(user)}</span>
              <br />
              <span>{user.email}</span>
              <br />
              <span>{user.phone}</span>
            </div>
            <hr />

            <SecuritySection
              user={user}
              phoneNumber={phoneNumber}
              onPhoneNumberChange={setPhoneNumber}
              onSavePhoneNumber={handleSavePhoneNumber}
              isSaving={isSaving}
              onResetMobile2FA={handleResetMobile2FA}
              resetAreYouSure={resetAreYouSure}
              mobile2faReset={mobile2faReset}
            />

            <hr />
            <div>
              <h5 className="font-size-14 mb-1">Merchants</h5>
              <div className="mb-3">
                <div className="table-responsive">
                  <Table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Is Owner</th>
                        <th>Options</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user?.merchants?.length === 0 && (
                        <Badge className="me-2 bg-danger">
                          Danger: This user has no merchants. Perhaps they have
                          been deleted?
                        </Badge>
                      )}
                      {user?.merchants?.map((merchant) => (
                        <MerchantRow
                          key={`${user.id}-${merchant.id}`}
                          merchant={merchant}
                          userId={user.id}
                          onViewMerchant={handleViewMerchant}
                        />
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
